import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../assets/logo-viewser.png';
import Clientes from '../pages/Admin/Clientes';
import DetalleCliente from '../pages/Admin/DetalleCliente';
import NuevoCliente from '../pages/Admin/NuevoCliente';
import Planes from '../pages/Admin/Planes';
import Estadisticas from '../pages/Admin/Estadisticas';
import Logs from '../pages/Admin/Logs';

const drawerWidth = 240;

const adminMenuItems = [
  { text: 'Clientes', icon: PeopleOutlineIcon, path: 'clientes', matchPaths: ['/admin/clientes'] },
  { text: 'Planes', icon: CreditCardIcon, path: 'planes', matchPaths: ['/admin/planes'] },
  { text: 'Estadísticas', icon: AssessmentOutlinedIcon, path: 'estadisticas', matchPaths: ['/admin/estadisticas'] },
  { text: 'Logs', icon: HistoryIcon, path: 'logs', matchPaths: ['/admin/logs'] },
];

const AdminDashboard = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(window.innerWidth >= 900);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 900);
      setIsDrawerOpen(window.innerWidth >= 900);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleOptionClick = () => {
    if (isSmallScreen) {
      toggleDrawer();
    }
  };

  const isActive = (item) => {
    return item.matchPaths.some(p => location.pathname.startsWith(p));
  };

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Botón menú móvil */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          top: '10px',
          left: '13px',
          zIndex: 1300,
        }}
      >
        <IconButton onClick={toggleDrawer}>
          <MenuIcon sx={{ fontSize: '32px', color: '#000' }} />
        </IconButton>
      </Box>

      <Drawer
        variant={isSmallScreen ? 'temporary' : 'permanent'}
        open={isDrawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: isSmallScreen ? '100%' : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '2px 0px 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Box sx={{ mt: { xs: 5, md: 0 } }}>
          {/* Logo */}
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Box
              component="img"
              src={Logo}
              alt="Logo"
              sx={{ width: '140px', mb: 1, mt: 3, mr: 6 }}
            />
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#0F52BD',
                fontWeight: 'bold',
                fontSize: '11px',
                letterSpacing: '1px',
                mt: -1,
              }}
            >
              ADMIN PANEL
            </Typography>
          </Box>

          {/* Navigation */}
          <List>
            {adminMenuItems.map((item) => {
              const active = isActive(item);
              const IconComponent = item.icon;
              return (
                <ListItem
                  key={item.path}
                  button
                  component={Link}
                  to={item.path}
                  selected={active}
                  onClick={handleOptionClick}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '30px',
                      marginRight: '8px',
                      marginLeft: '12px',
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: '28px',
                        color: active ? '#0F52BD' : '#4F4F4F',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '17px',
                      color: active ? '#000000' : '#4F4F4F',
                      fontWeight: 'normal',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Bottom section */}
        <Box>
          <List>
            <Divider sx={{ marginX: 2, backgroundColor: '#7894CC' }} />
            <ListItem button onClick={handleLogout} sx={{ cursor: 'pointer' }}>
              <ListItemIcon
                sx={{
                  minWidth: '30px',
                  marginRight: '8px',
                  marginLeft: '12px',
                }}
              >
                <LogoutOutlinedIcon sx={{ fontSize: '26px', color: '#4F4F4F' }} />
              </ListItemIcon>
              <ListItemText
                primary="Cerrar sesión"
                primaryTypographyProps={{
                  fontSize: '14px',
                  color: '#4F4F4F',
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={user?.email || 'Admin'}
                primaryTypographyProps={{
                  fontSize: '12px',
                  color: '#999',
                  textAlign: 'center',
                  style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: { xs: 3, md: 3, lg: 3 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Routes>
          <Route path="/" element={<Navigate to="clientes" />} />
          <Route path="clientes" element={<Clientes />} />
          <Route path="clientes/nuevo" element={<NuevoCliente />} />
          <Route path="clientes/:customerId" element={<DetalleCliente />} />
          <Route path="planes" element={<Planes />} />
          <Route path="estadisticas" element={<Estadisticas />} />
          <Route path="logs" element={<Logs />} />
          <Route path="*" element={<Navigate to="clientes" />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
